import type { EnrichedPart, ManufacturerProfile, PricingHistory, SupplierPriceUpdate } from "../domain/types";
import type { DemoGeneratorConfig } from "./generatorConfig";
import { roundTo, SeededRandom } from "./seededRandom";

const SUPPLIERS = ["Arrow", "Avnet", "Digi-Key", "Mouser", "TTI", "Future Electronics"];
const SOURCE_FEEDS = ["daily_supplier_feed", "datasheet_enrichment", "contract_file", "market_index"];

const baseCostForPart = (part: EnrichedPart, rng: SeededRandom): number => {
    if (part.category === "Semiconductor") return rng.float(1.5, 28, 4);
    if (part.category === "Capacitor") return rng.float(0.005, 1.2, 4);
    if (part.category === "Resistor") return rng.float(0.002, 0.4, 4);
    return rng.float(0.08, 5.5, 4);
};

export const generatePricing = (
    config: Pick<DemoGeneratorConfig, "pricingHistoryCount" | "priceUpdateCount">,
    rng: SeededRandom,
    parts: EnrichedPart[],
    manufacturers: ManufacturerProfile[],
): { pricingHistory: PricingHistory[]; priceUpdates: SupplierPriceUpdate[] } => {
    const manufacturerById = new Map(manufacturers.map((manufacturer) => [manufacturer.id, manufacturer] as const));
    const pricingHistory: PricingHistory[] = [];
    const historyByPartId = new Map<string, PricingHistory>();

    for (let index = 0; index < config.pricingHistoryCount; index += 1) {
        const part = parts[(index * 7) % parts.length]!;
        const manufacturer = manufacturerById.get(part.manufacturerId)!;
        const lastBookedCost = baseCostForPart(part, rng);
        const history: PricingHistory = {
            historyId: `HIST-${String(index + 1).padStart(6, "0")}`,
            partId: part.partId,
            manufacturerId: part.manufacturerId,
            ninetyDayAverageCost: roundTo(lastBookedCost * rng.float(0.92, 1.08, 4), 4),
            lastBookedCost,
            volatilityIndex: manufacturer.volatilityIndex,
            contractPricing: manufacturer.contractPricing,
        };

        pricingHistory.push(history);
        historyByPartId.set(part.partId, history);
    }

    const priceUpdates: SupplierPriceUpdate[] = [];

    for (let index = 0; index < config.priceUpdateCount; index += 1) {
        const part = parts[(index * 13) % parts.length]!;
        const manufacturer = manufacturerById.get(part.manufacturerId)!;
        const history =
            historyByPartId.get(part.partId) ??
            ({
                ninetyDayAverageCost: baseCostForPart(part, rng),
                lastBookedCost: baseCostForPart(part, rng),
            } as Pick<PricingHistory, "ninetyDayAverageCost" | "lastBookedCost">);

        const forcedBreach =
            manufacturer.id === "MFR-999" || index % 47 === 0
                ? manufacturer.reviewThresholdPct + rng.float(2, 22, 2)
                : index % 11 === 0
                  ? manufacturer.autoApproveThresholdPct + rng.float(1, 8, 2)
                  : rng.float(-8, manufacturer.autoApproveThresholdPct - 0.25, 2);
        const priceChangePct = roundTo(forcedBreach, 2);
        const newCost = roundTo(history.lastBookedCost * (1 + priceChangePct / 100), 4);

        priceUpdates.push({
            updateId: `PRICE-${String(index + 1).padStart(7, "0")}`,
            partId: part.partId,
            mpn: part.mpn,
            manufacturerId: part.manufacturerId,
            manufacturerName: part.manufacturerName,
            supplierName: rng.pick(SUPPLIERS),
            breakQuantity: rng.pick([1, 10, 100, 1000, 5000, 10000]),
            newCost,
            currency: "USD",
            updateDate: `2026-05-${String((index % 28) + 1).padStart(2, "0")}`,
            sourceFeed: rng.pick(SOURCE_FEEDS),
            confidenceScore: part.confidenceScore,
            lastBookedCost: history.lastBookedCost,
            ninetyDayAverageCost: history.ninetyDayAverageCost,
            priceChangePct,
        });
    }

    return { pricingHistory, priceUpdates };
};

